<div class="project-access">
  <!--
    One state at a time. With four resources in flight, one can still be loading
    while another has already failed — and both conditions include "nothing has
    rendered yet", so independent blocks would show a spinner and an error
    together. The failure is the more useful of the two.
  -->
  @if (store.isBlockingError()) {
    <div class="flex flex-col items-center justify-center p-6 text-center">
      <mat-icon class="text-error">error_outline</mat-icon>
      <span class="mt-3 text-sm text-neutral-300">Access could not be loaded.</span>
    </div>
  } @else if (store.isInitialLoading()) {
    <div class="flex flex-col items-center justify-center p-6 text-center">
      <mat-spinner diameter="32"></mat-spinner>
      <span class="mt-3 text-sm text-neutral-300">Loading access…</span>
    </div>
  }

  <!--
    A failed refetch keeps the last good rows on screen and says so, rather than
    replacing a panel the admin is working in with an error page.
  -->
  @if (store.error() && !store.isBlockingError()) {
    <div class="access-banner flex items-center gap-2" role="alert">
      <mat-icon class="text-error">error_outline</mat-icon>
      <span class="text-sm">This may be out of date — the last refresh failed.</span>
    </div>
  }

  <!-- ── Admin: configure every member ─────────────────────────────────── -->
  @if (canAdminister() && !store.isInitialLoading() && !store.isBlockingError()) {
    @if (store.members().length === 0) {
      <div class="flex flex-col items-center justify-center p-6 text-center">
        <mat-icon class="text-neutral-300">group</mat-icon>
        <span class="mt-3 text-sm text-neutral-300">No team members yet.</span>
      </div>
    }

    @for (member of store.members(); track member.userId) {
      <div class="access-member">
        <button
          type="button"
          class="access-member-header"
          [attr.aria-expanded]="expandedUserId() === member.userId"
          (click)="toggleExpanded(member.userId)"
        >
          <mat-icon class="access-chevron">
            {{ expandedUserId() === member.userId ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          <span class="access-member-name">{{ member.userName }}</span>
          <span class="access-role">{{ member.roleName }}</span>
          @if (member.isOwner) {
            <mat-icon class="access-badge" matTooltip="Project owner">stars</mat-icon>
          }
          @if (member.viewScope === 'Granted' || member.editScope === 'Granted') {
            <span class="access-restricted-pill">restricted</span>
          }
        </button>

        @if (expandedUserId() === member.userId) {
          <div class="access-panel">
            <!--
              Reported here rather than at the top of the tab: the message names
              a rule the admin has to act on, and the member list scrolls, so a
              banner above it can be off-screen from the control that produced
              it. Keyed per control, so a second edit elsewhere neither wipes
              this message nor misattributes its own.
            -->
            @for (failure of store.messagesFor(member.userId); track failure.key) {
              <div class="access-banner flex items-center gap-2" role="alert">
                <mat-icon class="text-error">error_outline</mat-icon>
                <span class="text-sm">{{ failure.message }}</span>
              </div>
            }
            <!-- Scope axes -->
            <div class="access-section">
              <div class="access-row">
                <span class="access-label">Can see</span>
                @if (member.restrictable) {
                  <mat-radio-group
                    #viewScopeGroup="matRadioGroup"
                    class="access-radios"
                    [value]="member.viewScope"
                    [disabled]="store.isPending(scopesKey(member))"
                    (change)="onViewScopeChange(member, $event.value, viewScopeGroup)"
                  >
                    <mat-radio-button value="All">Everything</mat-radio-button>
                    <mat-radio-button value="Granted">Only granted</mat-radio-button>
                  </mat-radio-group>
                } @else {
                  <span class="access-fixed">{{ scopeLabel(member, member.viewScope) }}</span>
                }
              </div>

              <div class="access-row">
                <span class="access-label">Can edit</span>
                @if (member.restrictable) {
                  <mat-radio-group
                    #editScopeGroup="matRadioGroup"
                    class="access-radios"
                    [value]="member.editScope"
                    [disabled]="store.isPending(scopesKey(member))"
                    (change)="onEditScopeChange(member, $event.value, editScopeGroup)"
                  >
                    <mat-radio-button value="All">Everything</mat-radio-button>
                    <mat-radio-button value="Granted">Only granted</mat-radio-button>
                  </mat-radio-group>
                } @else {
                  <span class="access-fixed">{{ scopeLabel(member, member.editScope) }}</span>
                }
              </div>

              <div class="access-row">
                <span class="access-label">Can publish versions</span>
                @if (member.baseline.publish) {
                  <span class="access-fixed">Yes, from their {{ member.roleName }} role</span>
                } @else {
                  <mat-slide-toggle
                    [checked]="member.publish.granted"
                    [disabled]="store.isPending(publishKey(member))"
                    (change)="onPublishChange(member, $event)"
                  ></mat-slide-toggle>
                }
              </div>
            </div>

            <!--
              Routines. A capability the role already confers gets no column:
              grants only add, so granting it would write a row that changes
              nothing and revoking it would take nothing away.
            -->
            <div class="access-section">
              <div class="access-section-title">Routines</div>
              @if (member.baseline.run && member.baseline.approve) {
                <div class="access-empty">
                  The {{ member.roleName }} role can already run and approve every routine in this
                  project.
                </div>
              } @else if (store.routines().length === 0) {
                <div class="access-empty">This project has no routines yet.</div>
              } @else {
                @if (member.baseline.run || member.baseline.approve) {
                  <div class="access-empty">
                    The {{ member.roleName }} role can already
                    {{ member.baseline.run ? 'run' : 'approve reviews for' }} every routine.
                  </div>
                }
                <table class="access-table">
                  <thead>
                    <tr>
                      <th scope="col">Routine</th>
                      @if (!member.baseline.run) {
                        <th scope="col" class="access-col">
                          May run
                          <span class="access-bulk">
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'RunRoutine'))"
                              (click)="checkAllRoutines(member, 'RunRoutine')"
                            >all</button>
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'RunRoutine'))"
                              (click)="uncheckAllRoutines(member, 'RunRoutine')"
                            >none</button>
                          </span>
                        </th>
                      }
                      @if (!member.baseline.approve) {
                        <th scope="col" class="access-col">
                          May approve
                          <span class="access-bulk">
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'ApproveReview'))"
                              (click)="checkAllRoutines(member, 'ApproveReview')"
                            >all</button>
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'ApproveReview'))"
                              (click)="uncheckAllRoutines(member, 'ApproveReview')"
                            >none</button>
                          </span>
                        </th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (routine of member.routines; track routine.routineId) {
                      <tr>
                        <td class="access-resource">{{ routine.name }}</td>
                        @if (!member.baseline.run) {
                          <td class="access-col">
                            <mat-checkbox
                              [checked]="routine.run.granted"
                              [disabled]="store.isPending(routineKey(member, routine, 'RunRoutine'))"
                              [attr.aria-label]="'May run ' + routine.name"
                              (change)="onRoutineToggle(member, routine, 'RunRoutine', $event)"
                            ></mat-checkbox>
                          </td>
                        }
                        @if (!member.baseline.approve) {
                          <td class="access-col">
                            <mat-checkbox
                              [checked]="routine.approve.granted"
                              [disabled]="store.isPending(routineKey(member, routine, 'ApproveReview'))"
                              [attr.aria-label]="'May approve reviews for ' + routine.name"
                              (change)="onRoutineToggle(member, routine, 'ApproveReview', $event)"
                            ></mat-checkbox>
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              }
            </div>

            <!-- Workflows: only meaningful once an axis is restricted -->
            @if (member.viewScope === 'Granted' || member.editScope === 'Granted') {
              <div class="access-section">
                <div class="access-section-title">Workflows</div>
                @if (store.workflows().length === 0) {
                  <div class="access-empty">This version has no workflows.</div>
                } @else {
                  <table class="access-table">
                    <thead>
                      <tr>
                        <th scope="col">Workflow</th>
                        <th scope="col" class="access-col">
                          May see
                          <span class="access-bulk">
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'ViewResource'))"
                              (click)="checkAllWorkflows(member, 'ViewResource')"
                            >all</button>
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'ViewResource'))"
                              (click)="uncheckAllWorkflows(member, 'ViewResource')"
                            >none</button>
                          </span>
                        </th>
                        <th scope="col" class="access-col">
                          May edit
                          <span class="access-bulk">
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'EditResource'))"
                              (click)="checkAllWorkflows(member, 'EditResource')"
                            >all</button>
                            <button
                              type="button"
                              [disabled]="store.isPending(columnKey(member, 'EditResource'))"
                              (click)="uncheckAllWorkflows(member, 'EditResource')"
                            >none</button>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (workflow of member.workflows; track workflow.name) {
                        <tr>
                          <td class="access-resource">
                            {{ workflow.name }}
                            @if (!workflow.inVersion) {
                              <span
                                class="access-restricted-pill"
                                matTooltip="Not in this version. The grant is still live because this workflow exists in another version — revoke it here."
                              >not in this version</span>
                            }
                          </td>
                          <td class="access-col">
                            <mat-checkbox
                              [checked]="workflow.view.granted"
                              [disabled]="store.isPending(workflowKey(member, workflow, 'ViewResource')) || !!workflow.view.implied"
                              [matTooltip]="cellTooltip(workflow.view)"
                              [attr.aria-label]="'May see ' + workflow.name"
                              (change)="onWorkflowToggle(member, workflow, 'ViewResource', $event)"
                            ></mat-checkbox>
                          </td>
                          <td class="access-col">
                            <mat-checkbox
                              [checked]="workflow.edit.granted"
                              [disabled]="store.isPending(workflowKey(member, workflow, 'EditResource'))"
                              [attr.aria-label]="'May edit ' + workflow.name"
                              (change)="onWorkflowToggle(member, workflow, 'EditResource', $event)"
                            ></mat-checkbox>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  }

  <!-- ── Everyone else: what am I permitted to do? ─────────────────────── -->
  @if (!canAdminister() && !store.isInitialLoading() && !store.isBlockingError()) {
    <div class="access-section">
      <div class="access-section-title">Your access</div>

      <div class="access-row">
        <span class="access-label">You can see</span>
        <span class="access-fixed">
          {{ myVisibilityRestricted() ? 'Only the workflows granted to you' : 'Everything in this project' }}
        </span>
      </div>

      @if (myVisibilityRestricted()) {
        @if (myVisibleWorkflows().length === 0) {
          <div class="access-empty">
            No workflows have been granted to you yet. A project admin can grant them.
          </div>
        } @else {
          <ul class="access-list">
            @for (name of myVisibleWorkflows(); track name) {
              <li>{{ name }}</li>
            }
          </ul>
        }
      }
    </div>

    <div class="access-section">
      <div class="access-section-title">What you may do</div>
      @if (myCapabilities().length === 0) {
        <div class="access-empty">
          You hold no additional permissions beyond your role in this project.
        </div>
      } @else {
        <ul class="access-list">
          @for (capability of myCapabilities(); track capability.label + capability.resource) {
            <li><span class="access-capability">{{ capability.label }}</span> {{ capability.resource }}</li>
          }
        </ul>
      }
    </div>
  }
</div>
